import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

export interface MusicTrack {
  id: string;
  name: string;
  nameHi: string;
  url: string;
}

interface MusicContextType {
  tracks: MusicTrack[];
  currentTrack: MusicTrack | null;
  isPlaying: boolean;
  volume: number;
  isLooping: boolean;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  setTrack: (track: MusicTrack) => void;
  setVolume: (volume: number) => void;
  setLooping: (loop: boolean) => void;
  nextTrack: () => void;
  prevTrack: () => void;
}

const MusicContext = createContext<MusicContextType | null>(null);

// Track list with URLs
const MUSIC_TRACKS: MusicTrack[] = [
  {
    id: 'ram-naam',
    name: 'Sri Ram Jay Ram',
    nameHi: 'श्री राम जय राम जय जय राम',
    url: 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/v5ck0gp0_Sri%20Ram%20Jay%20Ram%20Jay%20Jay%20Ram.mp3'
  },
  {
    id: 'sadashiv',
    name: 'Samb Sadashiv Naam Jap',
    nameHi: 'सांब सदाशिव नाम जप',
    url: 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/3xujmpel_Samb%20Sadashiv%20Naam%20Jap%20_%20Original%20Voice%20_%20%E0%A4%B8%E0%A4%BE%E0%A4%82%E0%A4%AC%20%E0%A4%B8%E0%A4%A6%E0%A4%BE%E0%A4%B6%E0%A4%BF%E0%A4%B5%20%E0%A4%A8%E0%A4%BE%E0%A4%AE%20%E0%A4%9C%E0%A4%AA%20_%20Shri%20Hit%20Prem.mp3'
  },
  {
    id: 'radha-naam',
    name: 'Radha Naam Pukar',
    nameHi: 'प्रातःकाल राधा नाम पुकार',
    url: 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/6wi25ut1_%E0%A4%AA%E0%A5%8D%E0%A4%B0%E0%A4%BE%E0%A4%A4%E0%A4%83%E0%A4%95%E0%A4%BE%E0%A4%B2%20%E0%A4%B0%E0%A4%BE%E0%A4%A7%E0%A4%BE%20%E0%A4%A8%E0%A4%BE%E0%A4%AE%20%E0%A4%AA%E0%A5%81%E0%A4%95%E0%A4%BE%E0%A4%B0--Radha%20Naam%20%20Jap%20by%20Shri%20Hit%20Premanand%20Mharaj%20ji%40pr_256k.mp3'
  },
  {
    id: 'harivansh',
    name: 'Radhavallabh Harivansh Naam',
    nameHi: 'राधावल्लभ श्री हरिवंश नाम जप',
    url: 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/izdxjctp_%E0%A4%B0%E0%A4%BE%E0%A4%A7%E0%A4%BE%E0%A4%B5%E0%A4%B2%E0%A5%8D%E0%A4%B2%E0%A4%AD%20%E0%A4%B6%E0%A5%8D%E0%A4%B0%E0%A5%80%20%E0%A4%B9%E0%A4%B0%E0%A4%BF%E0%A4%B5%E0%A4%82%E0%A4%B6%20%E0%A4%A8%E0%A4%BE%E0%A4%AE%20%E0%A4%9C%E0%A4%AA%E0%A5%A4%20%E0%A4%AE%E0%A4%B9%E0%A4%BE%E0%A4%B0%E0%A4%BE%E0%A4%9C%20%E0%A4%9C%E0%A5%80%20%E0%A4%95%E0%A5%87%20%E0%A4%B6%E0%A5%8D%E0%A4%B0%E0%A5%80%20%E0%A4%AE%E0%A5%81%E0%A4%96%20%E0%A4%B8%E0%A5%87%20%E0%A4%AE%E0%A4%A7%E0%A5%81%E0%A4%B0%20%E0%A4%B5%E0%A4%BE%E0%A4%A3%E0%A5%80%E0%A5%A4.mp3'
  },
  {
    id: 'ganga',
    name: 'Ganga River Sounds',
    nameHi: 'गंगा नदी की शांत ध्वनि',
    url: 'https://customer-assets.emergentagent.com/job_ef4eb1d2-e6b3-42b0-b647-461b6327fd91/artifacts/byt8pn6x_Relaxing%20%26%20Soothing%20River%20Sounds%20of%20Ganga%20River%20flowing%20through%20Rishikesh%20Ne_256k.mp3'
  }
];

export const MusicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasUserInteracted = useRef(false);
  const pendingAutoPlay = useRef(false);
  
  // Load saved preferences
  const [currentTrack, setCurrentTrack] = useState<MusicTrack>(() => {
    const savedTrackId = localStorage.getItem('golok_music_track');
    return MUSIC_TRACKS.find(t => t.id === savedTrackId) || MUSIC_TRACKS[0];
  });
  
  // Check if user wants music to play (default true for first visit)
  const [isPlaying, setIsPlaying] = useState(() => {
    const saved = localStorage.getItem('golok_music_playing');
    // Default to true for auto-play on first visit
    return saved === null ? true : saved === 'true';
  });
  
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem('golok_music_volume');
    return saved ? parseInt(saved) : 50;
  });
  
  const [isLooping, setIsLooping] = useState(() => {
    const saved = localStorage.getItem('golok_music_loop');
    return saved === null ? true : saved === 'true';
  });

  // Try to play audio (handles browser autoplay restrictions)
  const tryPlay = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.play().then(() => {
      hasUserInteracted.current = true;
      pendingAutoPlay.current = false;
    }).catch(e => {
      console.log('Play prevented, waiting for user interaction:', e.message);
      // Mark that we want to play when user interacts
      pendingAutoPlay.current = true;
    });
  }, []);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.loop = isLooping;
    audioRef.current.volume = volume / 100;
    
    // Handle track end
    audioRef.current.addEventListener('ended', () => {
      if (!isLooping) {
        // Play next track if not looping
        const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
        const nextIndex = (currentIndex + 1) % MUSIC_TRACKS.length;
        setCurrentTrack(MUSIC_TRACKS[nextIndex]);
      }
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Listen for user interaction to enable autoplay
  useEffect(() => {
    const handleInteraction = () => {
      hasUserInteracted.current = true;
      
      // If we have a pending autoplay request and user wants music, fulfill it now
      if (pendingAutoPlay.current && audioRef.current && isPlaying) {
        audioRef.current.play().then(() => {
          pendingAutoPlay.current = false;
          console.log('Background music started after user interaction');
        }).catch(() => {});
      }
    };

    // Listen continuously (not once) because audio might not be ready on first interaction
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, handleInteraction, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleInteraction);
      });
    };
  }, [isPlaying]);

  // Update audio source when track changes
  useEffect(() => {
    if (audioRef.current && currentTrack) {
      const wasPlaying = isPlaying;
      audioRef.current.src = currentTrack.url;
      audioRef.current.load();
      
      if (wasPlaying) {
        tryPlay();
      }
      
      localStorage.setItem('golok_music_track', currentTrack.id);
    }
  }, [currentTrack, tryPlay]);

  // Handle play/pause
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        tryPlay();
      } else {
        audioRef.current.pause();
        pendingAutoPlay.current = false;
      }
      localStorage.setItem('golok_music_playing', String(isPlaying));
    }
  }, [isPlaying, tryPlay]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      localStorage.setItem('golok_music_volume', String(volume));
    }
  }, [volume]);

  // Update loop
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = isLooping;
      localStorage.setItem('golok_music_loop', String(isLooping));
    }
  }, [isLooping]);

  const play = useCallback(() => {
    hasUserInteracted.current = true;
    setIsPlaying(true);
  }, []);
  
  const pause = useCallback(() => {
    setIsPlaying(false);
  }, []);
  
  const toggle = useCallback(() => {
    hasUserInteracted.current = true;
    setIsPlaying(prev => !prev);
  }, []);
  
  const setTrack = useCallback((track: MusicTrack) => {
    setCurrentTrack(track);
    setIsPlaying(true);
  }, []);

  const setVolume = useCallback((vol: number) => {
    setVolumeState(Math.max(0, Math.min(100, vol)));
  }, []);

  const setLooping = useCallback((loop: boolean) => {
    setIsLooping(loop);
  }, []);

  const nextTrack = useCallback(() => {
    const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
    const nextIndex = (currentIndex + 1) % MUSIC_TRACKS.length;
    setTrack(MUSIC_TRACKS[nextIndex]);
  }, [currentTrack, setTrack]);

  const prevTrack = useCallback(() => {
    const currentIndex = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
    const prevIndex = currentIndex === 0 ? MUSIC_TRACKS.length - 1 : currentIndex - 1;
    setTrack(MUSIC_TRACKS[prevIndex]);
  }, [currentTrack, setTrack]);

  // Media Session API for lock screen / notification controls
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!currentTrack) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.nameHi,
      artist: 'गोलोक दिव्य नाम कोष',
      album: 'भक्ति संगीत',
    });

    navigator.mediaSession.setActionHandler('play', () => {
      setIsPlaying(true);
    });
    navigator.mediaSession.setActionHandler('pause', () => {
      setIsPlaying(false);
    });
    navigator.mediaSession.setActionHandler('nexttrack', () => {
      const idx = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
      setTrack(MUSIC_TRACKS[(idx + 1) % MUSIC_TRACKS.length]);
    });
    navigator.mediaSession.setActionHandler('previoustrack', () => {
      const idx = MUSIC_TRACKS.findIndex(t => t.id === currentTrack.id);
      setTrack(MUSIC_TRACKS[idx === 0 ? MUSIC_TRACKS.length - 1 : idx - 1]);
    });
  }, [currentTrack, setTrack]);

  return (
    <MusicContext.Provider value={{
      tracks: MUSIC_TRACKS,
      currentTrack,
      isPlaying,
      volume,
      isLooping,
      play,
      pause,
      toggle,
      setTrack,
      setVolume,
      setLooping,
      nextTrack,
      prevTrack
    }}>
      {children}
    </MusicContext.Provider>
  );
};

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within MusicProvider');
  }
  return context;
};
